import React, { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import TenderActionButtons from './TenderActionButtons'
import { Query, Mutation } from '../../../api'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Trophy } from 'lucide-react'
import RoundSelect from './RoundSelect'
import AwardModal from './awardModal'
import BidsFiles from './BidsFiles'
import { numberFormat } from '../../../utils'

export const statuses = {
  PREPARATION: {
    title: 'Tender_statuses_in_preparation',
    badge: 'bg-blue-500/15 text-blue-700 border-blue-200',
    hex: '#3b82f6',
    textClass: 'text-blue-600',
  },
  ANOTHER_ROUND: {
    title: 'Tender_statuses_new_round',
    badge: 'bg-violet-500/15 text-violet-700 border-violet-200',
    hex: '#8b5cf6',
    textClass: 'text-violet-600',
  },
  HOD_APPROVAL: {
    title: 'Tender_statuses_waiting_for_hod_approval',
    badge: 'bg-amber-500/15 text-amber-700 border-amber-200',
    hex: '#f59e0b',
    textClass: 'text-amber-600',
  },
  HOD_REVISE: {
    title: 'Tender_statuses_hod_sent_for_revise',
    badge: 'bg-red-500/15 text-red-700 border-red-200',
    hex: '#ef4444',
    textClass: 'text-red-600',
  },
  CLIENT_APPROVAL: {
    title: 'Tender_statuses_waiting_for_client_approval',
    badge: 'bg-sky-500/15 text-sky-700 border-sky-200',
    hex: '#0ea5e9',
    textClass: 'text-sky-600',
  },
  CLIENT_REVISE: {
    title: 'Tender_statuses_client_sent_for_revise',
    badge: 'bg-red-500/15 text-red-700 border-red-200',
    hex: '#ef4444',
    textClass: 'text-red-600',
  },
  APPROVED: {
    title: 'Tender_statuses_approved_by_hod_and_client',
    badge: 'bg-teal-500/15 text-teal-700 border-teal-200',
    hex: '#14b8a6',
    textClass: 'text-teal-600',
  },
  RUNNING: {
    title: 'Tender_statuses_tender_is_running',
    badge: 'bg-emerald-500/15 text-emerald-700 border-emerald-200',
    hex: '#10b981',
    textClass: 'text-emerald-600',
  },
  AWARDED: {
    title: 'Tender_statuses_tender_awarded',
    badge: 'bg-yellow-500/15 text-yellow-700 border-yellow-200',
    hex: '#eab308',
    textClass: 'text-yellow-600',
  },
  CLOSED: {
    title: 'Tender_statuses_closed',
    badge: 'bg-slate-500/15 text-slate-700 border-slate-200',
    hex: '#64748b',
    textClass: 'text-slate-500',
  },
}

export default ({
  onRoundChange,
  noItemsInBreakdown,
  noBidders,
  tenderFormTouched,
  commencementDateInvalid,
  submissionDateInvalid,
  initialRound,
  hasUnsavedChanges,
}) => {
  const { t } = useTranslation()
  const { id, projectId } = useParams()
  const { data, loading } = useQuery(Query.TENDER, { variables: { id } })
  const [round, setRound] = useState(initialRound)
  const [awardModalOpen, setAwardModalOpen] = useState(false)

  const [tenderAction, { loading: tenderActionLoading }] = useMutation(Mutation.TENDER_ACTION, {
    variables: { id },
    onCompleted: () => {
      window.scrollTo(0, 0)
    },
    refetchQueries: [{ query: Query.TENDER, variables: { id } }],
  })

  useEffect(() => {
    initialRound && setRound(initialRound)
  }, [initialRound])

  const handleRoundChange = ({ target: { value } }) => {
    setRound(parseInt(value, 10))
    onRoundChange(parseInt(value, 10))
  }

  if (loading || !data || !data.tender) return null

  const { tender } = data || { tender: {} }

  if (tender.status !== 'AWARDED')
    return (
      <>
        <div className="xl:sticky xl:top-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('Tender_status_tender_actions_heading')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3 xl:flex-col xl:items-stretch">
                <TenderActionButtons
                  projectId={projectId}
                  disabled={noItemsInBreakdown || noBidders || hasUnsavedChanges}
                  datesInvalid={commencementDateInvalid || submissionDateInvalid}
                  loading={tenderActionLoading}
                  actions={tender.statusActions}
                  onAction={(t) => {
                    if (t === 'award') {
                      setAwardModalOpen(true)
                    } else {
                      tenderAction({ variables: { action: t } })
                    }
                  }}
                />
                <div className="ml-auto xl:ml-0">
                  <RoundSelect onChange={handleRoundChange} round={round} tender={tender} />
                </div>
              </div>

              {hasUnsavedChanges && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {t('Tender_unsaved_changes_alert', 'Please save your changes before changing tender status')}
                  </AlertDescription>
                </Alert>
              )}

              {noItemsInBreakdown && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t('Price_breakdown_no_items_alert')}</AlertDescription>
                </Alert>
              )}
              {noBidders && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t('Price_breakdown_no_bidders_alert')}</AlertDescription>
                </Alert>
              )}
              {(commencementDateInvalid || submissionDateInvalid) && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t('Price_breakdown_dates_not_valid_alert')}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
        <AwardModal
          open={awardModalOpen}
          onChange={(result) => {
            tenderAction({ variables: { action: 'award', payload: { bidderId: result } } })
            setAwardModalOpen(false)
          }}
          onClose={() => {
            setAwardModalOpen(false)
          }}
          bids={tender.bids.filter((b) => b.round === round && b.priceBreakdown)}
        />
      </>
    )

  if (tender.status === 'AWARDED') {
    if (tender.winnerBidder) {
      let winningBid = tender.bids.filter(
        (b) => b.round === tender.round && b.bidder.id === tender.winnerBidder.id,
      )[0]

      return (
        <div className="xl:sticky xl:top-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base text-amber-600">
                <Trophy className="h-5 w-5" />
                {t('Tender_status_winner_heading', 'Winner')}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <span className="font-medium">{t('Tender_status_winner_name', 'Name')}:</span>
                <span>{winningBid.bidder.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">{t('Tender_status_winner_total', 'Total price')}:</span>
                <span>{numberFormat.format(winningBid.totalPrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">{t('Tender_status_winner_last_change', 'Last change')}:</span>
                <span>{t('formatter_date2', { date: winningBid.updatedAt })}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">{t('Tender_status_winner_files', 'Files')}:</span>
                <BidsFiles files={winningBid.files} />
              </div>
              {tender.round > 1 && (
                <div className="mt-3 pt-3 border-t">
                  <RoundSelect onChange={handleRoundChange} round={round} tender={tender} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )
    }
  }
  return null
}
